/**
 * t298u: desktop feed activity cards as posts about their subject. Header: the subject's icon, its title in bold and
 * its ref muted; one muted meta line (building · area · priority · status · reported by X · when); the description and
 * first photo; at most one muted "Latest: …" line; then reactions, then the latest one or two comments with "View all
 * N comments". No pills. A click anywhere outside the controls opens the subject.
 */
import { useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Building2, ImageIcon, MessageCircle } from 'lucide-react';
import { feedLatestText, viewAllCommentsLabel, type ActivitySeg, type FeedPost, type FeedPostLatest, type FeedThread } from '@tradeoutlet/shared';
import { useTaskComments } from '@tradeoutlet/data';
import { api } from '@/lib/api';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { RelativeTime } from '@/components/time/RelativeTime';
import { ItemReactions } from '@/components/reactions/ItemReactions';
import { PersonButton } from '@/features/people/PersonDrawer';
import { CommentsPreview, useCommentCount } from '@/features/threads/CommentsPreview';
import { useFileUrl } from '@/features/ops/common';
import { cardOpenHandlers } from '@/lib/cardOpen';
import { CLOSED_BODY, CLOSED_FADE, CLOSED_TITLE, FeedClosureStrip } from './FeedClosureStrip';
import { TaskComments } from './feedShared';

/** How many comments a post shows under it before "View all N comments". */
export const POST_COMMENTS = 2;

export function Segs({ segs, strong }: { segs: ActivitySeg[]; strong?: boolean }) {
  return <>{segs.map((x, n) => (typeof x === 'string' ? <span key={n}>{x}</span>
    : <PersonButton key={n} userId={x.id} name={x.name} className={clsx('rounded hover:underline', strong ? 'font-semibold text-slate-700 dark:text-slate-200' : 'font-medium text-slate-600 dark:text-slate-300')}>{x.name}</PersonButton>))}</>;
}

/** The issue's first photo as a small thumbnail (signed URL, fetched lazily). */
export function PostPhoto({ incidentId, att, className }: { incidentId: string; att: string; className?: string }) {
  const ws = useWorkspaceId();
  const q = useFileUrl(['incident', incidentId, att], () => api.ops.incidentFile(ws!, incidentId, att));
  return q.data
    ? <img src={q.data.url} alt="Photo on the issue" className={clsx('shrink-0 rounded-lg object-cover ring-1 ring-slate-200 dark:ring-slate-700', className)} data-testid="post-photo" />
    : <span className={clsx('grid shrink-0 place-items-center rounded-lg bg-slate-100 dark:bg-slate-800', className)} data-testid="post-photo"><ImageIcon className="h-4 w-4 text-slate-400" aria-hidden /></span>;
}

export function PostLatest({ l, className }: { l: FeedPostLatest; className?: string }) {
  const text = feedLatestText(l);
  return (
    <p className={clsx('text-xs text-slate-500 dark:text-slate-400', className)} data-testid="post-latest" aria-label={text}>
      Latest: {l.who ? <><Segs segs={[l.who]} /> {l.what}</> : <>{l.what.charAt(0).toUpperCase()}{l.what.slice(1)}</>} <RelativeTime iso={l.at} />
    </p>
  );
}

/** t701u: the building's short code, bold and muted in the card's top right; the full name on hover. */
export function BuildingCode({ b }: { b: { code: string; name: string } }) {
  return <span className="inline-flex shrink-0 items-center gap-1 text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300" title={b.name} aria-label={`Building: ${b.name}`} data-testid="post-building-code"><Building2 className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" aria-hidden data-testid="post-building-icon" />{b.code}</span>;
}

export function PostCard({ post, icon, tone, onOpen, testId, entity, children, extra }: {
  post: FeedPost; icon: ReactNode; tone: string; onOpen: () => void; testId: string; entity: string;
  /** Reactions and comments. */ children: ReactNode; /** e.g. RSVP buttons, under the description. */ extra?: ReactNode;
}) {
  const p = post;
  // t362u: done or closed: a strip across the top and the rest quieter.
  const closed = Boolean(p.closure);
  return (
    <article className="cursor-pointer rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900" data-testid={testId} data-entity={entity} data-post data-closed={closed || undefined} {...cardOpenHandlers(onOpen)}>
      {p.closure && <FeedClosureStrip closure={p.closure} className="-mx-4 -mt-4 mb-3 rounded-t-[11px]" />}
      <div className="flex items-start gap-3">
        <span className={clsx('grid h-8 w-8 shrink-0 place-items-center rounded-full', tone, closed && CLOSED_FADE)} aria-hidden>{icon}</span>
        <div className="min-w-0 flex-1">
          <h3 className={clsx('text-[15px] leading-snug', closed && CLOSED_TITLE)} data-testid="post-title">
            <button type="button" onClick={onOpen} className="text-left font-semibold hover:underline">{p.title}</button>
            {p.ref && <span className="ml-1.5 text-sm font-normal text-slate-500" data-testid="post-ref">{p.ref}</span>}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400" data-testid="post-meta">
            {p.meta.map((m, n) => <span key={n}>{n > 0 && ' · '}<Segs segs={m} /></span>)}
            {p.when && <>{p.meta.length > 0 && ' · '}<RelativeTime iso={p.when} /></>}
          </p>
        </div>
        {p.building && <BuildingCode b={p.building} />}
      </div>
      {(p.description || p.photo) && (
        <div className="mt-2 flex items-start gap-3 pl-11">
          {p.description && <p className={clsx('line-clamp-3 min-w-0 flex-1 whitespace-pre-line text-sm text-slate-700 dark:text-slate-300', closed && CLOSED_BODY)} data-testid="post-description">{p.description}</p>}
          {p.photo && <PostPhoto incidentId={p.photo.incident_id} att={p.photo.attachment_id} className={clsx('h-20 w-20', !p.description && 'mr-auto', closed && CLOSED_FADE)} />}
        </div>
      )}
      {extra && <div className="cursor-auto pl-11" data-no-card-open>{extra}</div>}
      {p.latest && <PostLatest l={p.latest} className="mt-2 pl-11" />}
      <div className="cursor-auto pl-11" data-no-card-open>{children}</div>
    </article>
  );
}

/** Reactions, then the item's own discussion: its latest one or two comments, "View all N comments", and the reply box. */
export function PostThreadFooter({ type, id, count }: { type: string; id: string; count?: number }) {
  const n = useCommentCount(type, id, count);
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="mt-2 flex items-center gap-2" data-testid="item-footer">
        <ItemReactions type={type} id={id} />
        {!open && !(n ?? 0) && (
          <button type="button" onClick={() => setOpen(true)} data-testid="comment-button"
            className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"><MessageCircle className="h-4 w-4" />Comment</button>
        )}
      </div>
      <CommentsPreview type={type} entityId={id} count={count} open={open} hideButton autoFocus={open} max={POST_COMMENTS} />
    </>
  );
}

/** A task's reactions, its latest one or two comments ("View all N comments" opens the task) and a reply box. */
export function TaskPostFooter({ taskId, total, th, onOpen }: { taskId: string; total: number | null; th?: FeedThread; onOpen: () => void }) {
  const known = total ?? th?.count ?? 0;
  const comments = useTaskComments(known > 0 ? taskId : null);
  const { byId } = useMembers();
  const [open, setOpen] = useState(false);
  const list = (comments.data ?? []).filter(c => !c.deleted_at);
  const all = Math.max(known, list.length);
  const shown = list.slice(-POST_COMMENTS);
  const more = viewAllCommentsLabel(all, shown.length);
  return (
    <>
      <div className="mt-2 flex items-center gap-2" data-testid="item-footer">
        <ItemReactions type="task" id={taskId} />
        {!open && <button type="button" onClick={() => setOpen(true)} data-testid="comment-button"
          className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"><MessageCircle className="h-4 w-4" />Comment</button>}
      </div>
      {shown.length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-2 dark:border-slate-800" data-testid="feed-comments">
          {more && <button type="button" onClick={onOpen} className="px-1 text-xs font-semibold text-slate-600 hover:underline dark:text-slate-300" data-testid="view-all-comments">{more}</button>}
          {shown.map(c => {
            const name = byId.get(c.author_id)?.display_name ?? 'Someone';
            return (
              <p key={c.id} className="rounded-lg bg-slate-50 px-3 py-1.5 text-sm dark:bg-slate-800/60" data-testid="post-comment">
                <PersonButton userId={c.author_id} name={name} className="rounded font-semibold hover:underline">{name}</PersonButton>{' '}
                <span className="whitespace-pre-wrap text-slate-600 dark:text-slate-300">{c.body}</span> <span className="text-xs text-slate-500"><RelativeTime iso={c.created_at} /></span>
              </p>
            );
          })}
        </div>
      )}
      {open && <TaskComments autoFocus onSent={() => { setOpen(false); void comments.refetch(); }}
        th={{ entity_type: 'task', entity_id: taskId, title: '', link: '', count: 0, actors: [], comments: [], reply: { kind: 'task' } }} />}
    </>
  );
}
